import React, { useState, useEffect } from 'react';

const StockKeywordAnalyzer = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [keywordData, setKeywordData] = useState(null);
  const [analysisInfo, setAnalysisInfo] = useState(null);

  // 불용어 목록 (분석에서 제외할 단어들)
  const stopWords = [
    '기자', '뉴스', '연합뉴스', '머니투데이', '한국경제', '매일경제', '이데일리',
    '오전', '오후', '증시', '마감', '대비', '상승', '하락', '전일', '전년',
    '기업', '회사', '그룹', '증권', '은행', '금융', '투자', '매매',
    '시장', '경제', '정책', '정부', '관련', '통한', '대한', '위한',
    '있다', '없다', '있다고', '없다고', '했다', '했다고', '된다', '된다고',
    '이번', '올해', '내년', '작년', '지난', '앞으로', '앞으로는',
    '분기', '월', '일', '주', '년', '시간', '분', '초',
    '억', '만', '천', '조', '원', '달러', '엔', '위안',
    '퍼센트', '프로', '%', '증가', '감소', '성장', '축소'
  ];

  // 한글 텍스트에서 키워드 추출
  const extractKeywords = (text) => {
    if (!text) return [];
    
    // 한글 2글자 이상 단어 추출 (정규표현식)
    const koreanWords = text.match(/[가-힣]{2,}/g) || [];
    
    // 불용어 제거 및 빈도수 계산
    const wordCount = {};
    koreanWords.forEach(word => {
      if (!stopWords.includes(word) && word.length >= 2) {
        wordCount[word] = (wordCount[word] || 0) + 1;
      }
    });

    // 빈도수 기준으로 정렬하고 상위 50개 선택
    const sortedWords = Object.entries(wordCount)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 50)
      .map(([word, count]) => [word, count]);

    return sortedWords;
  };

  // 뉴스 데이터 분석
  const analyzeNewsData = async () => {
    setLoading(true);
    setError(null);
    setKeywordData(null);
    setAnalysisInfo(null);

    try {
      // NewsAPI 호출 - 한국 경제 뉴스 100개 가져오기
      const params = new URLSearchParams({
        endpoint: 'top-headlines',
        country: 'kr',
        category: 'business',
        pageSize: '100'
      });


      const response = await fetch(`/api/news/dynamic?${params.toString()}`);
      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || '뉴스 데이터 가져오기 실패');
      }

      const articles = data.result.articles || [];
      
      if (articles.length === 0) {
        throw new Error('분석할 뉴스 데이터가 없습니다.');
      }

      // 모든 뉴스 제목과 설명을 하나의 텍스트로 합치기
      const allText = articles
        .map(article => `${article.title || ''} ${article.description || ''}`)
        .join(' ');

      // 키워드 추출
      const keywords = extractKeywords(allText);

      if (keywords.length === 0) {
        throw new Error('분석할 키워드를 찾을 수 없습니다.');
      }

      setKeywordData(keywords);
      setAnalysisInfo({
        totalArticles: articles.length,
        totalKeywords: keywords.length,
        analyzedAt: new Date().toLocaleString('ko-KR'),
        topKeywords: keywords.slice(0, 10).map(([word, count]) => ({ word, count }))
      });


    } catch (err) {
      console.error('❌ 키워드 분석 실패:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // 키워드 색상 결정 함수
  const getKeywordColor = (count) => {
    if (count > 10) return '#e74c3c'; // 빨간색 (매우 중요)
    if (count > 7) return '#f39c12';  // 주황색 (중요)
    if (count > 4) return '#f1c40f';  // 노란색 (보통)
    return '#95a5a6'; // 회색 (덜 중요)
  };

  // 키워드 크기 결정 함수
  const getKeywordSize = (count) => {
    if (count > 10) return '2.5rem';
    if (count > 7) return '2rem';
    if (count > 4) return '1.5rem';
    return '1.2rem';
  };

  return (
    <div className="stock-keyword-analyzer">
      <div className="page-header">
        <h1>📈 오늘의 주식 키워드</h1>
        <p>한국 경제 뉴스를 분석하여 시장의 핵심 키워드를 워드 클라우드로 시각화합니다.</p>
      </div>

      <div className="control-section">
        <button 
          className="analyze-button"
          onClick={analyzeNewsData}
          disabled={loading}
        >
          {loading ? '분석 중...' : '🔍 키워드 분석 시작'}
        </button>
        
        {analysisInfo && (
          <div className="analysis-info">
            <div className="info-item">
              <span className="label">분석된 뉴스:</span>
              <span className="value">{analysisInfo.totalArticles}개</span>
            </div>
            <div className="info-item">
              <span className="label">추출된 키워드:</span>
              <span className="value">{analysisInfo.totalKeywords}개</span>
            </div>
            <div className="info-item">
              <span className="label">분석 시간:</span>
              <span className="value">{analysisInfo.analyzedAt}</span>
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="error-section">
          <h3>❌ 오류</h3>
          <div className="error-message">{error}</div>
        </div>
      )}

      {loading && (
        <div className="loading-section">
          <div className="loading-spinner"></div>
          <p>뉴스 데이터를 분석하고 있습니다...</p>
        </div>
      )}

      {keywordData && (
        <div className="result-section">
          <div className="keyword-cloud-container">
            <h3>🎯 시장 핵심 키워드</h3>
            <div className="keyword-cloud">
              {keywordData.map(([word, count], index) => (
                <span
                  key={index}
                  className="keyword-tag"
                  style={{
                    fontSize: getKeywordSize(count),
                    color: getKeywordColor(count),
                    fontWeight: count > 7 ? '700' : '600'
                  }}
                  title={`${word}: ${count}회 언급`}
                >
                  {word}
                </span>
              ))}
            </div>
          </div>

          {analysisInfo && (
            <div className="keyword-list">
              <h4>📊 상위 키워드 순위</h4>
              <div className="keyword-grid">
                {analysisInfo.topKeywords.map((item, index) => (
                  <div key={index} className="keyword-item">
                    <span className="rank">#{index + 1}</span>
                    <span className="word">{item.word}</span>
                    <span className="count">{item.count}회</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="info-section">
        <h3>📋 서비스 설명</h3>
        <div className="info-content">
          <div className="info-group">
            <h4>🎯 분석 방법</h4>
            <ul>
              <li><strong>데이터 수집:</strong> NewsAPI를 통해 한국 경제 뉴스 100개 수집</li>
              <li><strong>텍스트 분석:</strong> 뉴스 제목과 설명에서 한글 키워드 추출</li>
              <li><strong>불용어 제거:</strong> 의미 없는 단어들 필터링</li>
              <li><strong>빈도수 계산:</strong> 각 키워드의 등장 횟수 집계</li>
              <li><strong>시각화:</strong> 워드 클라우드로 핵심 키워드 표시</li>
            </ul>
          </div>
          
          <div className="info-group">
            <h4>💡 해석 방법</h4>
            <ul>
              <li><strong>큰 글씨:</strong> 시장에서 가장 많이 언급되는 핵심 이슈</li>
              <li><strong>빨간색:</strong> 매우 중요한 시장 동향</li>
              <li><strong>주황색:</strong> 중요한 투자 이슈</li>
              <li><strong>노란색:</strong> 주목할 만한 시장 변화</li>
              <li><strong>회색:</strong> 일반적인 경제 용어</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockKeywordAnalyzer;
